// Open an OAuth popup centered over the current browser window. Centering is
// relative to this window (not the primary monitor's origin) so it lands on
// the screen the app is on in multi-monitor setups, instead of the top-left
// default `window.open` gives when no left/top is supplied.
//
// Use outerWidth/outerHeight (the whole browser window incl. chrome), not
// innerWidth/innerHeight (the content viewport): screenX/screenY is the
// window's frame origin, so centering against the content size instead shifts
// the popup left/down by the window border + scrollbar width.
const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 700;

export function openCenteredPopup(url: string, name: string): Window | null {
    const baseLeft = window.screenLeft ?? window.screenX;
    const baseTop = window.screenTop ?? window.screenY;
    const parentW = window.outerWidth || screen.availWidth;
    const parentH = window.outerHeight || screen.availHeight;
    const left = Math.round(Math.max(0, baseLeft + (parentW - POPUP_WIDTH) / 2));
    const top = Math.round(Math.max(0, baseTop + (parentH - POPUP_HEIGHT) / 2));
    return window.open(
        url,
        name,
        `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},scrollbars=yes`,
    );
}

// The result arrives by postMessage or a localStorage `storage` event from our callback page, both of
// which work under Cross-Origin-Opener-Policy. Returns a cleanup() that also runs once a result arrives.
export function runOAuthPopup(opts: {
    url: string;
    name: string;
    storageKey: string;
    messageType: string;
    onResult: (data: any) => void;
}): () => void {
    const { url, name, storageKey, messageType, onResult } = opts;
    // Drop any stale result so an old same-key storage event can't be replayed.
    localStorage.removeItem(storageKey);
    openCenteredPopup(url, name);

    const isOurs = (type: unknown) => type === messageType || type === `${messageType}-error`;
    const finish = (data: any) => { cleanup(); onResult(data); };

    const messageHandler = (e: MessageEvent) => {
        if (e.origin !== window.location.origin) return;
        if (isOurs(e.data?.type)) finish(e.data);
    };
    const storageHandler = (e: StorageEvent) => {
        if (e.key === storageKey && e.newValue) {
            try { finish(JSON.parse(e.newValue)); } catch {}
        }
    };
    const cleanup = () => {
        window.removeEventListener('message', messageHandler);
        window.removeEventListener('storage', storageHandler);
    };

    window.addEventListener('message', messageHandler);
    window.addEventListener('storage', storageHandler);
    return cleanup;
}
